import { AddIcon } from "@minket/brand/icons/add";
import { TopicsIcon } from "@minket/brand/icons/topics";
import { Button } from "@minket/ui/components/button";
import { EmptyState } from "@minket/ui/components/empty-state";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { lazy } from "react";
import { OnDemand } from "@/components/audience/kit";
import { PreferenceSpecimen } from "@/components/audience/preference-specimen";
import { TopicLedger } from "@/components/audience/topic-ledger";
import { UnavailableAction } from "@/components/page/action";
import { GhostTable } from "@/components/page/ghost";
import { Page } from "@/components/page/page";
import { ProjectScope } from "@/components/page/scope";
import { QueryView } from "@/components/page/states";
import { endpoints } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiQuery } from "@/lib/query";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/topics")({
  component: Topics,
});

const load = () => import("@/components/audience/topic-dialogs.ondemand");
const TopicDialog = lazy(() => load().then((m) => ({ default: m.TopicDialog })));

const columns = ["Topic", "Default", "Subscribers"] as const;
const will = "Each topic will be listed here with its default and its subscribers.";

function Topics() {
  const { project } = useWorkspace();
  const canManage = useCan("audience.manage");
  const pending = { label: "New topic", icon: AddIcon };
  return (
    <Page
      title="Topics"
      action={
        !project ? (
          <UnavailableAction {...pending} reason="Needs a project" />
        ) : !canManage ? (
          <UnavailableAction {...pending} reason="Viewers cannot create topics" />
        ) : (
          <OnDemand
            load={load}
            button={(props) => (
              <Button {...props}>
                <AddIcon size={20} className="size-5" />
                New topic
              </Button>
            )}
            dialog={(props) => <TopicDialog projectId={project.id} {...props} />}
          />
        )
      }
    >
      <ProjectScope
        will={will}
        icon={<TopicsIcon />}
        preview={(busy) => <GhostTable columns={columns} label="topics" busy={busy} />}
      >
        {(current) => (
          <TopicList projectId={current.id} project={current.name} canManage={canManage} />
        )}
      </ProjectScope>
    </Page>
  );
}

function TopicList({
  projectId,
  project,
  canManage,
}: {
  projectId: string;
  project: string;
  canManage: boolean;
}) {
  const query = useQuery(apiQuery(endpoints.topics, { projectId }));
  const preview = (busy: boolean) => <GhostTable columns={columns} label="topics" busy={busy} />;
  return (
    <QueryView query={query} noun="topics" preview={preview}>
      {(page) =>
        page.data.length === 0 ? (
          <EmptyState
            data-state="empty"
            icon={<TopicsIcon />}
            title="No topics yet"
            description="With topics, a reader can stop one kind of mail and keep the rest; without them, their only choice is to unsubscribe from all marketing email."
            preview={preview(false)}
          />
        ) : (
          <div className="grid min-w-0 items-start gap-x-10 gap-y-8 min-[1180px]:grid-cols-[minmax(0,1fr)_22rem]">
            <TopicLedger projectId={projectId} topics={page.data} canManage={canManage} />
            <div className="min-[1180px]:sticky min-[1180px]:top-6">
              <PreferenceSpecimen project={project} topics={page.data} />
            </div>
          </div>
        )
      }
    </QueryView>
  );
}
